import { useEffect, useState } from "react";

import type { ApiClient } from "../../../infrastructure/http/apiClient";
import { loadAppointmentsForWindow } from "../../appointments";
import { loadOnboardingSetup } from "../../onboarding";
import { toAgendaError, toSetupError } from "../domain/errors";
import { buildTodayWindow } from "../domain/time";
import type { AgendaState, AgendaWindow, AuthenticatedShellPageProps, SetupState, ShellState } from "../domain/shellTypes";

export function useSetupAgenda({
  apiClient,
  initialSetup,
  initialAppointments,
  initialAgendaError,
  initialAccessToken = "",
  isProfessionalUser,
  sessionToken,
  state
}: Pick<AuthenticatedShellPageProps, "initialSetup" | "initialAppointments" | "initialAgendaError" | "initialAccessToken"> & {
  apiClient: ApiClient;
  isProfessionalUser: boolean;
  sessionToken: string;
  state: ShellState;
}) {
  const [setupState, setSetupState] = useState<SetupState>(
    initialSetup ? { status: "success", setup: initialSetup } : state.status === "success" && initialAccessToken ? { status: "loading" } : { status: "idle" }
  );
  const [agendaState, setAgendaState] = useState<AgendaState>(() => {
    const initialWindow = buildTodayWindow(initialSetup?.timezone ?? "UTC");

    if (initialAppointments) {
      return { status: "success", appointments: initialAppointments, window: initialWindow };
    }

    if (initialAgendaError) {
      return { status: "error", title: initialAgendaError.title, message: initialAgendaError.message, retryable: true, window: initialWindow };
    }

    if (initialSetup && initialSetup.readiness.complete && initialAccessToken) {
      return { status: "loading", window: initialWindow };
    }

    return { status: "idle" };
  });

  const setupForAgenda = setupState.status === "success" || setupState.status === "saving" || setupState.status === "error"
    ? setupState.setup
    : undefined;
  const setupCompleteForAgenda = setupForAgenda ? setupForAgenda.readiness.complete : false;

  useEffect(() => {
    if (state.status !== "success" || !sessionToken || isProfessionalUser) {
      return;
    }

    let ignore = false;
    setSetupState({ status: "loading" });
    loadOnboardingSetup(apiClient)
      .then((setup) => {
        if (!ignore) {
          setSetupState({ status: "success", setup });
        }
      })
      .catch((error) => {
        if (!ignore) {
          setSetupState(toSetupError(error));
        }
      });

    return () => {
      ignore = true;
    };
  }, [apiClient, isProfessionalUser, sessionToken, state.status]);

  useEffect(() => {
    if (isProfessionalUser || !setupCompleteForAgenda) {
      setAgendaState({ status: "idle" });
      return;
    }

    if (!sessionToken) {
      return;
    }

    let ignore = false;
    const window = buildTodayWindow(setupForAgenda?.timezone ?? "UTC");
    setAgendaState({ status: "loading", window });

    loadAppointmentsForWindow(apiClient, window)
      .then((appointments) => {
        if (!ignore) {
          setAgendaState({ status: "success", appointments, window });
        }
      })
      .catch((error) => {
        if (!ignore) {
          setAgendaState(toAgendaError(error, window));
        }
      });

    return () => {
      ignore = true;
    };
  }, [apiClient, isProfessionalUser, sessionToken, setupCompleteForAgenda, setupForAgenda]);

  async function reloadAgenda(window?: AgendaWindow, resourceID?: string) {
    const agendaWindow = window ?? (agendaState.status === "idle" ? buildTodayWindow(setupForAgenda?.timezone ?? "UTC") : agendaState.window);
    const requestWindow = { ...agendaWindow, resourceID: resourceID || undefined };
    setAgendaState({ status: "loading", window: agendaWindow });

    try {
      const appointments = await loadAppointmentsForWindow(apiClient, requestWindow);
      setAgendaState({ status: "success", appointments, window: agendaWindow });
    } catch (error) {
      setAgendaState(toAgendaError(error, agendaWindow));
    }
  }

  function resetSetupAgenda() {
    setSetupState({ status: "idle" });
    setAgendaState({ status: "idle" });
  }

  return { agendaState, reloadAgenda, resetSetupAgenda, setupState };
}
